import React, { createContext, useContext, useState } from "react";
import { Game } from "../types/ScheduleTypes";
import { Club } from "../types/ClubTypes";
import { useDepthChart } from "./DepthChartContext";
import {
  DepthChart,
  DepthChartPlayer,
  DepthChartPlayerAssignment,
  DepthChartPosition,
} from "../types/DepthChartTypes";
import { AllSideTabs } from "../constants";
import { GameLog } from "../types/LogTypes";

interface LoggerContextType {
  selectedGame: Game | null;
  selectedTab: number;
  selectedSide: string;
  clubScore: number;
  oppScore: number;
  down: number;
  distance: number;
  clubTOL: number;
  oppTOL: number;
  quarter: number;
  timeRemaining: string;
  ballOn: number | null;
  possession: Club | null;
  gameStarted: boolean;
  gameLog: GameLog | null;

  setSelectedGame: (game: Game | null) => void;
  setSelectedTab: (tab: number) => void;
  setSelectedSide: (side: string) => void;
  setClubScore: (score: number) => void;
  setOppScore: (score: number) => void;
  setDistance: (distance: number) => void;
  setDown: (down: number) => void;
  setClubTOL: (TOL: number) => void;
  setOppTOL: (TOL: number) => void;
  setQuarter: (quarter: number) => void;
  setTimeRemaining: (time: string) => void;
  setBallOn: (ballOn: number | null) => void;
  setPossession: (possession: Club | null) => void;
  setGameStarted: (gameStarted: boolean) => void;
  setClubPossession: () => void;
  setOppPossession: () => void;
  onStartGame: (startSide: "KICKOFF" | "KICKOFF_RETURN") => void;
  setGameLog: (gameLog: GameLog | null) => void;
}

export const LoggerContext = createContext<LoggerContextType | undefined>(
  undefined
);

export const LoggerProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [selectedGame, setSelectedGame] = useState<Game | null>(null);
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [selectedSide, setSelectedSide] = useState<string>("OFFENSE");
  const [clubScore, setClubScore] = useState<number>(0);
  const [oppScore, setOppScore] = useState<number>(0);
  const [down, setDown] = useState<number>(-1);
  const [distance, setDistance] = useState<number>(-1);
  const [clubTOL, setClubTOL] = useState<number>(3);
  const [oppTOL, setOppTOL] = useState<number>(3);
  const [quarter, setQuarter] = useState<number>(-1);
  const [timeRemaining, setTimeRemaining] = useState<string>("-");
  const [ballOn, setBallOn] = useState<number | null>(null);
  const [possession, setPossession] = useState<Club | null>(null);
  const [gameStarted, setGameStarted] = useState<boolean>(false);
  const [gameLog, setGameLog] = useState<GameLog | null>(null);
  const { getDepthChartsForGame } = useDepthChart();

  const setClubPossession = () => {
    if (selectedGame?.user_club) {
      setPossession(selectedGame.user_club);
    }
  };

  const setOppPossession = () => {
    if (selectedGame?.opponent_club) {
      setPossession(selectedGame.opponent_club);
    }
  };

  const onStartGame = (startSide: "KICKOFF" | "KICKOFF_RETURN") => {
    if (selectedGame?.id) {
      getDepthChartsForGame(selectedGame?.id);
    }
    setGameStarted(true);
    setOppPossession();
    setQuarter(1);
    setTimeRemaining("12:00");
    setSelectedSide(startSide);
    setBallOn(35);
    setSelectedTab(AllSideTabs.findIndex((tab) => tab.value === startSide));
  };

  return (
    <LoggerContext.Provider
      value={{
        selectedGame,
        selectedTab,
        selectedSide,
        clubScore,
        oppScore,
        down,
        distance,
        clubTOL,
        oppTOL,
        quarter,
        timeRemaining,
        ballOn,
        possession,
        gameStarted,
        gameLog,
        setSelectedGame,
        setSelectedTab,
        setSelectedSide,
        setClubScore,
        setOppScore,
        setDown,
        setDistance,
        setClubTOL,
        setOppTOL,
        setQuarter,
        setTimeRemaining,
        setBallOn,
        setPossession,
        setGameStarted,
        setClubPossession,
        setOppPossession,
        onStartGame,
        setGameLog,
      }}
    >
      {children}
    </LoggerContext.Provider>
  );
};

export const useLogger = () => {
  const context = useContext(LoggerContext);
  if (context === undefined) {
    throw new Error("useLogger must be used within a LoggerProvider");
  }
  return context;
};
